/** Impact-category row reused by the existing ProductSummaryTableCol table. */
export interface ImpactCategoryResult {
  key?: string;
  indicator: string;
  measure: number;
  unit: string;
}

/** Window used to resolve mapping drawing changes for a version. */
export interface ChangedTimeWindow {
  from: string | null;
  to: string | null;
}

export interface VersionHistoryUser {
  id: string;
  name?: string;
  email?: string;
}

/** One calculation run (a ProductSummary document) belonging to a mapping version. */
export interface VersionHistoryRecord {
  id: string;
  calculatedAt: string;
  calculateVersion?: string;
  climateChange: { measure?: number; unit?: string } | null;
  totalImpact: ImpactCategoryResult[];
  lastEditedBy: VersionHistoryUser | null;
  /**
   * The edit reason that led to this calculation (resolved server-side from changereasonlogs by
   * this record's own window), or null when none was logged -- e.g. a calculation from before
   * this field existed. Optional so a response from before the backend added it still type-checks.
   */
  reason?: string | null;
  /** This calculation's own window -- bounded by its own timestamp and the preceding calculation's. */
  changedTime: ChangedTimeWindow;
}

/** A mapping version with its nested calculation history. Server-owned shape. */
export interface VersionHistoryGroup {
  versionId: string | null;
  versionNumber: number | null;
  versionName: string | null;
  reason: string | null;
  reportingYear: number | null;
  /** Commercial metadata owned by this version alone. Null on versions predating the fields. */
  numberOfUnits: number | null;
  pricePerUnit: number | null;
  isDefault: boolean;
  isCalculated: boolean;
  calculationCount: number;
  /** false when isDefault -- hides the Activate button. */
  canActivate: boolean;
  isLegacy: boolean;
  drawingToolId: string | null;
  lastEditedAt: string | null;
  lastEditedBy: VersionHistoryUser | null;
  changedTime: ChangedTimeWindow;
  records: VersionHistoryRecord[];
}

/** PaginateResponseDto envelope, paginated by VERSION. */
export interface GroupedVersionHistoryResponse {
  result: {
    groups: VersionHistoryGroup[];
    drawingToolId: string | null;
  };
  total: number;
  page: number;
  pageSize: number;
}

export interface GroupedVersionHistoryParams {
  productId: string;
  salesUnitId: string;
  pageNumber: number;
  pageSize: number;
}

/** PATCH /mapping-versions/:versionId/metadata */
export interface UpdateVersionMetadataPayload {
  versionId: string;
  versionName?: string;
  reportingYear?: number | null;
  numberOfUnits?: number | null;
  pricePerUnit?: number | null;
}

export interface UpdateVersionMetadataResponse {
  _id: string;
  versionNumber: number;
  versionName: string;
  isDefault: boolean;
  isCalculated: boolean;
  reportingYear: number | null;
  numberOfUnits: number | null;
  pricePerUnit: number | null;
}

/**
 * The three version-details fields as react-hook-form holds them. Strings, not numbers:
 * FormSelect stringifies its value and FormInputNumber stores a cleaned string, so parsing
 * belongs at the payload boundary (see buildVersionDetailsPayload), not in the form.
 */
export interface VersionDetailsFormValues {
  reportingYear: string;
  numberOfUnits: string;
  pricePerUnit: string;
}

/** A submitted draft. Same shape as the form values -- named apart to mark the boundary. */
export type VersionDetailsDraft = VersionDetailsFormValues;

/** Pre-formatted strings for the collapsed version bar's inline summary. */
export interface VersionDetailsSummaryParts {
  year: string;
  units: string;
  price: string;
}

export enum VersionBadgeTone {
  Active = "active",
  Calculated = "calculated",
  NotCalculated = "notCalculated"
}

export enum VersionHistoryAccess {
  Allowed = "allowed",
  AverageProduct = "averageProduct",
  SupplierRole = "supplierRole"
}

export interface VersionHistoryAccessResult {
  access: VersionHistoryAccess;
  isAllowed: boolean;
  /** Null when allowed. */
  message: string | null;
}
